import { generateStableRequestId, TOKEN_CONFIG } from '@/consts/token'
import { refreshTeamToken } from '@/utils/tokenRefresh'

/**
 * Manages team token refresh operations with retry logic and request queueing
 */
class TokenRefreshManager {
  private refreshingPromise: Promise<string> | null = null
  private retryCountMap: Map<string, number> = new Map()
  private cleanupTimers: Map<string, ReturnType<typeof setTimeout>> = new Map()

  /**
   * Refresh team token using available tokens (primary team token or platform token)
   * Prevents concurrent refreshes by returning the same promise if already refreshing
   */
  async refreshTeamToken(): Promise<string> {
    // If already refreshing, return the same promise
    if (this.refreshingPromise) {
      return this.refreshingPromise
    }

    // Start new refresh
    this.refreshingPromise = refreshTeamToken()
      .then((token) => {
        // Success - clear the refreshing promise
        this.refreshingPromise = null
        return token
      })
      .catch((error) => {
        // Failed - clear the refreshing promise so next request can retry
        this.refreshingPromise = null
        throw error
      })

    return this.refreshingPromise
  }

  /**
   * Check if token refresh is currently in progress
   */
  isRefreshing(): boolean {
    return this.refreshingPromise !== null
  }

  /**
   * Get retry count for a specific request
   */
  getRetryCount(requestId: string): number {
    return this.retryCountMap.get(requestId) || 0
  }

  /**
   * Increment retry count for a request
   * Returns the new count and schedules automatic cleanup
   */
  incrementRetryCount(requestId: string): number {
    const currentCount = this.getRetryCount(requestId)
    const newCount = currentCount + 1
    this.retryCountMap.set(requestId, newCount)

    // Schedule cleanup to prevent memory leak
    this.scheduleCleanup(requestId)

    return newCount
  }

  /**
   * Check if request has exceeded max retry count
   */
  hasExceededMaxRetries(requestId: string): boolean {
    return this.getRetryCount(requestId) >= TOKEN_CONFIG.MAX_TOKEN_REFRESH_RETRIES
  }

  /**
   * Reset retry count for a request (e.g., after success)
   * Also clears any pending cleanup timers
   */
  resetRetryCount(requestId: string): void {
    this.retryCountMap.delete(requestId)

    // Clear cleanup timer if exists
    const timer = this.cleanupTimers.get(requestId)
    if (timer) {
      clearTimeout(timer)
      this.cleanupTimers.delete(requestId)
    }
  }

  /**
   * Schedule automatic cleanup of retry count after 60 seconds
   * Prevents memory leak from abandoned requests
   */
  private scheduleCleanup(requestId: string): void {
    // Clear existing timer if any
    const existingTimer = this.cleanupTimers.get(requestId)
    if (existingTimer) {
      clearTimeout(existingTimer)
    }

    // Schedule new cleanup
    const timer = setTimeout(() => {
      this.retryCountMap.delete(requestId)
      this.cleanupTimers.delete(requestId)
    }, 60000) // 60 seconds

    this.cleanupTimers.set(requestId, timer)
  }

  /**
   * Clear all retry counts (e.g., after logout)
   * Also clears all cleanup timers
   */
  clearAllRetryCounts(): void {
    this.retryCountMap.clear()

    // Clear all cleanup timers
    this.cleanupTimers.forEach((timer) => clearTimeout(timer))
    this.cleanupTimers.clear()
  }

  /**
   * Generate stable request ID from URL and method
   * Uses centralized implementation from token config
   */
  static generateRequestId(url?: string, method?: string): string {
    return generateStableRequestId(url, method)
  }
}

// Export singleton instance
export const tokenRefreshManager = new TokenRefreshManager()
